/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/typedef */

import { Inject, Injectable } from '@angular/core';
import { UIRouter } from '@uirouter/core';
import { HttpErrorResponse, HttpRequest, HttpResponse} from '@angular/common/http';
import { CimaToken, Util, XmStore, XmStoreUtil } from 'core/services';
import { StoreAction } from 'store/actions';
import { Logger } from 'store/logger/models';
import { IXMOptions } from 'core/interfaces';
import { CONFIG_TOKEN, LoggerEvent, LoggerOptions } from 'core/constants';
@Injectable({
    providedIn: 'root'
})
export class LoggerService {
    private xmStore: XmStore;
    private config: IXMOptions;
    private uiRouter: UIRouter;
    private cimaToken: CimaToken;

    constructor(@Inject(CONFIG_TOKEN) config: IXMOptions, xmStore: XmStore, uiRouter: UIRouter, cimaToken: CimaToken) {
        Object.assign(this, { config, xmStore, uiRouter, cimaToken });
    }

    /****************************
     * Service level log *
     ****************************/
    public info(httpResponse: HttpResponse<object>, httpRequest: HttpRequest<object>, startTime: number): void {
        if (this.cimaToken.isLoggedIn || this.isUnAuthApiCall(httpRequest?.url)){
            const endTime: number = performance.now();
            const logData: object[] = [{
                appname: this.getAppName(),
                entryType: LoggerEvent.MEASURE,
                startTime: startTime,
                duration: endTime - startTime,
                event: LoggerEvent.MEASURE,
                data: {
                    request: {
                        method: httpRequest.method,
                        data: httpRequest.body && JSON.stringify(Util.maskData(httpRequest.body))
                    },
                    response: {
                        status: httpResponse.status,
                        text: httpResponse.body && JSON.stringify(Util.maskData(httpResponse.body))
                    },
                    url: httpRequest.urlWithParams,
                    ...(this.isActivationURL(httpRequest.url, httpResponse) && {error: {
                        code: this.getActivationErrorCode(httpRequest.url, httpResponse)
                    }
                    })
                },
                mode: httpRequest?.url.includes('wearables/eligibility/requests') ? this.getMode(httpResponse) : undefined,
                denialReason: httpRequest?.url.includes('wearables/eligibility/requests') ? this.getDenialReason(httpResponse) : undefined,
                conditionalReason: httpRequest?.url.includes('wearables/eligibility/requests') ? this.getConditionalReason(httpResponse) : undefined,
                _time: Date.now(),
                component: this.xmComponent(),
                env: this.config.LOG_ENV,
                page: this.pageName(),
                uuid: Util.generateUuid(),
                uri: window.location.href,
                custGuid: this.getCustGuid()
            }];
            XmStoreUtil.defaultCatch(this.xmStore.persist<Logger>(StoreAction.POST_LOGGER, { payLoad: logData }));
        }
        
    }

    /****************************
     * Content level log *
     ****************************/
    public cmsInfo(httpResponse: HttpResponse<object>, httpRequest: HttpRequest<object>, startTime: number): void {
        if (this.cimaToken.isLoggedIn){
            const endTime: number = performance.now();
            const logData: object[] = [{
                appname: this.getAppName(),
                entryType: LoggerEvent.MEASURE,
                startTime: startTime,
                duration: endTime - startTime,
                data: {
                    request: {
                        method: httpRequest.method,
                        data: JSON.stringify(httpRequest.body)
                    },
                    response: {
                        status: httpResponse.status,
                        text: JSON.stringify(httpResponse.body)
                    },
                    url: httpRequest.urlWithParams
                },
                _time: Date.now(),
                component: this.xmComponent(),
                env: this.config.LOG_ENV,
                page: this.pageName(),
                uuid: Util.generateUuid(),
                uri: window.location.href,
                custGuid: this.getCustGuid()
            }];

            XmStoreUtil.defaultCatch(this.xmStore.persist<Logger>(StoreAction.POST_LOGGER, { payLoad: logData }));
        }
    }

    /****************************
     * Page level log *
     ****************************/
    public pageInfo(pageInfo: string): void {
        if (this.cimaToken.isLoggedIn){
            const startTime: number = performance.now();
            const logData: object[] = [{
                appname: this.getAppName(),
                entryType: LoggerEvent.OTHERS,
                startTime: startTime,
                duration: 0,
                data: {
                    message: pageInfo
                },
                _time: Date.now(),
                component: this.xmComponent(),
                env: this.config.LOG_ENV,
                page: this.pageName(),
                uuid: Util.generateUuid(),
                custGuid: this.getCustGuid()
            }];

            XmStoreUtil.defaultCatch(this.xmStore.persist<Logger>(StoreAction.POST_LOGGER, { payLoad: logData }));
        }
    }

    /****************************
     * Service error level log *
     ****************************/
    public error(httpError: HttpErrorResponse, httpRequest: HttpRequest<object>, startTime: number): void {
        if (this.cimaToken.isLoggedIn || this.isUnAuthApiCall(httpRequest?.url)){
            const errorMessage = this.isNowMobile() ? httpError?.error?.error?.errorMessage : httpError?.error?.message;
            const errorCode = this.isNowMobile() ? httpError?.error?.error?.errorCode : httpError?.error?.code;
            const endTime: number = performance.now();
            const logData: object[] = [{
                appname: this.getAppName(),
                entryType: LoggerEvent.ERROR,
                startTime: startTime,
                duration: endTime - startTime,
                event: LoggerEvent.MEASURE,
                data: {
                    request: {
                        method: httpRequest.method,
                        data: httpRequest.body && JSON.stringify(httpRequest.body)
                    },
                    response: {
                        status: httpError.status,
                        text: httpError.message
                    },
                    error: {
                        name: httpError.name,
                        message: errorMessage,
                        code: errorCode
                    },
                    url: httpRequest.urlWithParams
                },
                _time: Date.now(),
                component: this.xmComponent(),
                env: this.config.LOG_ENV,
                page: this.pageName(),
                uuid: Util.generateUuid(),
                custGuid: this.getCustGuid()
            }];
            XmStoreUtil.defaultCatch(this.xmStore.persist<Logger>(StoreAction.POST_LOGGER, { payLoad: logData }));
        }
    }

    /****************************
     * Script error level log *
     ****************************/
    public scriptError(httpError: HttpErrorResponse | string): void {
        if (this.cimaToken.isLoggedIn){
            const startTime: number = performance.now();
            const logData: object[] = [{
                appname: this.getAppName(),
                entryType: LoggerEvent.ERROR,
                startTime: startTime,
                duration: 0,
                event: LoggerEvent.ERROR,
                data: httpError,
                _time: Date.now(),
                component: this.xmComponent(),
                env: this.config.LOG_ENV,
                page: this.pageName(),
                uuid: Util.generateUuid(),
                custGuid: this.getCustGuid()
            }];

            XmStoreUtil.defaultCatch(this.xmStore.persist<Logger>(StoreAction.POST_LOGGER, { payLoad: logData }));
        }
    }

    private xmComponent(): string {
        const name: string = this.uiRouter.globals.transition ? this.uiRouter.globals.transition.to().name : this.defaultComponent();
        
        return this.isNowMobile() ? name : `xm-${name}`;
    }

    private pageName(): string {
        return this.uiRouter.globals.transition ? this.uiRouter.globals.transition.to().name : this.defaultComponent();
    }

    private getCustGuid(): string {
        return this.cimaToken.isLoggedIn ? this.cimaToken.decodedIdToken.sub : undefined; 
    }

    private getActivationErrorCode(url: string, response: HttpResponse<any>): string {
        if (url?.includes('activation') && response.body) {
            return response.body.errorCode;
        } else {
            return undefined;
        }
    }

    private getAppName(): string {
        return this.isNowMobile() ? LoggerOptions.NOW_APP_NAME : LoggerOptions.APP_NAME; 
    }

    private defaultComponent(): string {
        return this.isNowMobile() ? LoggerOptions.NOW_COMPONENT : LoggerOptions.COMPONENT;
    }

    private isNowMobile(): boolean {
        return window.location.href.includes('/now');
    }

    private isActivationURL(url: string, response: HttpResponse<any>): boolean {
        return url.includes('activation') && response.body.errorCode;
    }

    private isUnAuthApiCall(url: string): boolean {
        return url?.includes('/mdn/lookup') || url?.includes('/account/quickpayment');
    }

    private getMode(response: HttpResponse<any>): string {
        return response.body.mode;
    }

    private getDenialReason(response: HttpResponse<any>): string {
        const denialReason = response.body?.eligibility?.denialReason || [];

        return denialReason.map((reason: any) => ({
            reasonCode: reason.reasonCode || '',
            reasonDescription: reason.reasonDescription || ''
        }));
    }

    private getConditionalReason(response: HttpResponse<any>): string {
        const conditionalReason = response.body?.eligibility?.conditionalReason || [];

        return conditionalReason.map((reason: any) => ({
            reasonCode: reason.reasonCode || '',
            reasonDescription: reason.reasonDescription || ''
        }));
    }
}
